import React, { FC, useCallback, useState } from 'react';
import Svg from 'svg-inline-react';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { MultiValue } from 'react-select';
import FrSearchSvg from '@src/svgs/fr-search.svg';
import { OutsideClick } from '@src/components/OutsideClick';
import { useGetAutoCompleteUsersSearchPatternNoPaginationLazyQuery } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { SearchPattern } from '@src/containers/Source/interfaces/searchPattern.interface';
import { useRecentSearches } from '@src/containers/Source/hooks/useRecentSearches';
import { highLightText } from '@src/containers/Source/utils/highlightText';
import { RecentSearches } from '@src/containers/Source/RecentSearches';
import FrFilters2Svg from '@src/svgs/fr-filters-2.svg';
import { Option } from '@src/components/Select/interfaces/option.interface';
import { QueryParams } from '@src/containers/Source/interfaces/queryParams.interface';
import { SearchField } from './SearchFIeld';
import './styles.scss';

export interface SearchProps {
  onDropdownToggle: (isOpen: boolean) => void;
  onFilterClick: () => void;
  onQueryParamsSelect: (queryParams: QueryParams) => void;
  onSelect: (searchPatterns: SearchPattern[]) => void;
  queryParams: QueryParams;
  values: SearchPattern[];
}

export const Search: FC<SearchProps> = ({
  onSelect,
  values,
  queryParams,
  onQueryParamsSelect,
  onDropdownToggle,
  onFilterClick,
}) => {
  const [searchValue, setSearchValue] = useState('');

  const [isOpen, setIsOpen] = useState(false);

  const [getAutoCompleteUsersSearchPattern, { data }] = useGetAutoCompleteUsersSearchPatternNoPaginationLazyQuery();

  const { recentSearches, setRecentSearches, removeRecentSearch } = useRecentSearches();

  const autoCompleteOptions: SearchPattern[] = (data?.autoCompleteUsersSearchPattern.result || []).map(item => {
    return {
      id: item,
      pattern: item,
    };
  });

  const options: SearchPattern[] = [
    { id: `searchValue-${searchValue}`, pattern: searchValue?.trim() },
    ...autoCompleteOptions,
  ];

  const onToggle = (isOpen: boolean) => {
    setIsOpen(isOpen);
    onDropdownToggle(isOpen);
  };

  const handleSelect = (newOptions: MultiValue<Option>) => {
    const selectedSearchPatterns = options.filter(option => newOptions.some(({ value }) => value === option.id));

    setRecentSearches({
      ...queryParams,
      searchPatterns: selectedSearchPatterns,
    });

    const newPatterns = newOptions.map(option => {
      return {
        id: option.value,
        pattern: option.label,
      };
    });

    onSelect(newPatterns);

    trackEvent({
      context: MetricContext.TALENT_AGENT_PAGE,
      interaction: MetricAction.SELECT_SEARCH_PATTERNS,
      payload: {
        'Filter Type': 'Keyword',
        'Filter Value': options.map(({ pattern }) => pattern).join(', '),
      },
    });
  };

  const onOptionClick = (option: SearchPattern) => {
    onSelect([...values, option]);

    setSearchValue('');

    onToggle(false);
  };

  const onGetAutoCompleteUsersSearchPattern = useCallback(
    debounce((searchPattern: string) => {
      getAutoCompleteUsersSearchPattern({
        variables: {
          paginationOptions: {
            limit: 5,
          },
          searchPattern: searchPattern,
        },
      });
    }, 350),
    [],
  );

  const onSearch = (searchValue: string) => {
    const doesSearchValueExist = !!searchValue?.trim().length;

    if (doesSearchValueExist) {
      onGetAutoCompleteUsersSearchPattern(searchValue);
    }

    setSearchValue(searchValue);

    onToggle(doesSearchValueExist);
  };

  return (
    <OutsideClick addOutsideClickListener={isOpen} onOutsideClick={() => onToggle(false)}>
      <div className="talent-pipeline-sidebar-search">
        <Svg
          className={classnames(
            'talent-pipeline-sidebar-search__icon-search',
            isOpen && 'talent-pipeline-sidebar-search__icon-search--active',
          )}
          src={FrSearchSvg}
        />

        <SearchField
          searchValue={searchValue}
          setIsOpen={onToggle}
          onSearch={onSearch}
          options={options}
          onOptionClick={onOptionClick}
          handleSelect={handleSelect}
          values={values}
        />

        <div className="talent-pipeline-sidebar-search__icon-filters-wrapper" onClick={onFilterClick}>
          <Svg className="talent-pipeline-sidebar-search__icon-filters" src={FrFilters2Svg} />
        </div>

        {isOpen && (!!searchValue.length || !!recentSearches?.length) && (
          <div className="talent-pipeline-sidebar-search__dropdown-wrapper">
            <div className="talent-pipeline-sidebar-search__section-title">Suggestions</div>

            {options.map(
              option =>
                !!option.pattern && (
                  <div
                    className={classnames(
                      'talent-pipeline-sidebar-search__option',
                      option.id === 'searchValue' && 'talent-pipeline-sidebar-search__option--bold',
                    )}
                    key={option.id}
                    onClick={() => onOptionClick(option)}
                  >
                    {highLightText(searchValue, option.pattern)}
                  </div>
                ),
            )}

            <div className="talent-pipeline-sidebar-search__recent-wrapper">
              <div className="talent-pipeline-sidebar-search__section-title">Recent Searches</div>

              <RecentSearches
                onQueryParamsSelect={onQueryParamsSelect}
                recentSearches={recentSearches || []}
                removeRecentSearch={removeRecentSearch}
              />
            </div>
          </div>
        )}
      </div>
    </OutsideClick>
  );
};
